import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {ConfigPageComponent} from "../../../../../core/shared/mvs-page/config-page.component";
import {WidgetConfigInterface} from "../../../../../core/shared/mvs-page/interface/widget-config-page.interface";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {NavigationItem} from "../../../../../core/shared/dto/navigation/navigation-item";
import {MvsObjectNavigationEntry} from "../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {PageHeaderConfig} from "../../../../../core/shared/service/page/page-header-config.interface";
import {MvsConfigHeaderComponent} from "../../../../../core/shared/components/mvs-config-header/mvs-config-header.component";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {WidgetDataParam} from "../../../../../core/shared/widget/widget-data-param";
import {QlQueryDto} from "../../../../../core/shared/ql/data/dto/ql-query.dto";

@Component({
    selector: 'mvs-ig-config-page',
    templateUrl: './ig-config.page.html',
    styleUrls: ['./ig-config.page.scss'],
    standalone: false
})
export class IgConfigPage extends ConfigPageComponent implements OnInit {

    /**
     * Child widget aliases are class constants because the same alias MUST be used in the
     * unfiltered and the filtered state of the usage log — the alias is the identity the
     * table state (column widths, sorting, visibility) is persisted under.
     */
    static readonly cAccessTokenUsedWidgetAlias = 'config.ig.IgAccessTokenUsed.table';
    static readonly cPassTokenUsedWidgetAlias = 'config.ig.IgPassTokenUsed.table';

    accessTokenUsedWidget: WidgetData;
    passTokenUsedWidget: WidgetData;

    selectedAccessTokenId: number;
    selectedPassTokenId: number;

    defaultLabel: string = "Integration Codes Konfiguration";

    override widgets: { [key: string]: WidgetConfigInterface } = {
        igAccessTokenWidget: {
            alias: 'config.ig.IgAccessToken.table',
            label: 'Zugangstoken',
            entity: 'ig.IgAccessToken',
            widgetData: new WidgetData(),
            createFunction: (): WidgetData => {
                /**
                 * AI:
                 * Status: "in progress"
                 * Type: Widget
                 * SubType: Configuration
                 * Reason: Editing surface for ig.IgAccessToken. Until now the entity was only
                 *   reachable through generic object navigation, so the token settings
                 *   (allowedIpAddresses, recordUsage, apiRoutes, expiryDate) had no place a clerk
                 *   could open. Metadata driven table, therefore new backend fields appear here
                 *   without a column list to maintain. Row click opens the object form.
                 */
                return WidgetFactory.createWidgetTableEntityQl(
                    'config.ig.IgAccessToken.table',
                    'Zugangstoken',
                    'ig.IgAccessToken',
                    'Keine Zugangstoken vorhanden',
                    [],
                    [new Sorting('e.id', false)],
                    true
                );
            }
        },
        igAccessTokenSelectableWidget: {
            alias: 'config.ig.IgAccessToken.selectable',
            label: 'Zugangstoken',
            entity: 'ig.IgAccessToken',
            widgetData: new WidgetData(),
            createFunction: (): WidgetData => {
                // The projection is explicit and deliberately excludes e.token: the sidebar must
                // not merely hide the raw bearer secret, it must never receive it. Only e.id and
                // the owning user's name leave the server for this widget.
                const qlQueryDto: QlQueryDto = {
                    name: 'config.ig.IgAccessToken.selectable',
                    type: 'view',
                    filters: [],
                    start: {
                        name: 'ig.IgAccessToken',
                        as: 'e',
                        type: 'entity',
                        fields: [{name: 'id'}],
                        joins: [
                            {
                                joinType: 'left',
                                type: 'entity',
                                name: 'user',
                                as: 'usr',
                                fields: [{name: 'id'}, {name: 'name'}]
                            }
                        ]
                    },
                    sortings: [new Sorting('usr.name', true)]
                };

                /**
                 * AI:
                 * Status: "in progress"
                 * Type: Widget
                 * SubType: Configuration
                 * Reason: Sidebar picker over ig.IgAccessToken for the usage log tab. Selecting a
                 *   token narrows the ig.IgAccessTokenUsed list on the right to that token, which
                 *   is the forensic question the log exists for ("who used THIS token, when").
                 *
                 *   It used to display e.token, and that column is the raw bearer secret: a picker
                 *   renders every row of it at once, on a screen a clerk keeps open all day. The
                 *   identity a human actually recognises is the owning user, so the query joins
                 *   um.User and each row is labelled with the user name plus the token id — the id
                 *   because one user may hold several tokens and the name alone would not tell
                 *   them apart. The secret stays reachable only through the object form on the
                 *   Zugangstoken tab, where it is shown one record at a time on purpose.
                 */
                return WidgetFactory.createWidgetListQl(
                    'config.ig.IgAccessToken.selectable',
                    'Zugangstoken',
                    'selectable',
                    qlQueryDto,
                    // attributeToDisplay stays the SINGLE searchable/sortable attribute — the
                    // selectable builds its search filter and its sort from this one field, so it
                    // must be the user name for "search over user name" to reach the server.
                    WidgetDataParam.create('attributeToDisplay', 'usr.name'),
                    // showTemplate + attributeToDisplayTemplate are the composed-label mechanism:
                    // '@'-prefixed tokens, '#' meaning '.', rendered one span per attribute.
                    WidgetDataParam.create('showTemplate', true),
                    WidgetDataParam.create('attributeToDisplayTemplate', '@usr#name @e#id'),
                    WidgetDataParam.create('showSearch', true)
                );
            }
        },
        igPassTokenWidget: {
            alias: 'config.ig.IgPassToken.table',
            label: 'Pass-Token',
            entity: 'ig.IgPassToken',
            widgetData: new WidgetData(),
            createFunction: (): WidgetData => {
                /**
                 * AI:
                 * Status: "in progress"
                 * Type: Widget
                 * SubType: Configuration
                 * Reason: Editing surface for ig.IgPassToken, the end user counterpart of the
                 *   access token. Same reason as the access token table: the entity was only
                 *   reachable through generic object navigation. Metadata driven, row click opens
                 *   the object form.
                 */
                return WidgetFactory.createWidgetTableEntityQl(
                    'config.ig.IgPassToken.table',
                    'Pass-Token',
                    'ig.IgPassToken',
                    'Keine Pass-Token vorhanden',
                    [],
                    [new Sorting('e.id', false)],
                    true
                );
            }
        },
        igPassTokenSelectableWidget: {
            alias: 'config.ig.IgPassToken.selectable',
            label: 'Pass-Token',
            entity: 'ig.IgPassToken',
            widgetData: new WidgetData(),
            createFunction: (): WidgetData => {
                // Explicit projection, and e.token is not in it — same reason as the access token
                // sidebar above. The joined attribute is 'email', not 'name': eu.EuUser has NO
                // name attribute (it implements EntityUser only; its human identifier is the
                // unique e-mail), unlike the um.User behind an access token.
                const qlQueryDto: QlQueryDto = {
                    name: 'config.ig.IgPassToken.selectable',
                    type: 'view',
                    filters: [],
                    start: {
                        name: 'ig.IgPassToken',
                        as: 'e',
                        type: 'entity',
                        fields: [{name: 'id'}],
                        joins: [
                            {
                                joinType: 'left',
                                type: 'entity',
                                name: 'user',
                                as: 'usr',
                                fields: [{name: 'id'}, {name: 'email'}]
                            }
                        ]
                    },
                    sortings: [new Sorting('usr.email', true)]
                };

                /**
                 * AI:
                 * Status: "in progress"
                 * Type: Widget
                 * SubType: Configuration
                 * Reason: Sidebar picker over ig.IgPassToken for the pass token usage log tab.
                 *   Selecting a token narrows the ig.IgPassTokenUsed list to that token.
                 *
                 *   Displays the owning end user, not e.token, for the same reason as the access
                 *   token sidebar: the token column is the raw bearer value and a picker list
                 *   would render every one of them at once. Here the label is the user's e-mail,
                 *   because that is the only human identifier eu.EuUser carries.
                 */
                return WidgetFactory.createWidgetListQl(
                    'config.ig.IgPassToken.selectable',
                    'Pass-Token',
                    'selectable',
                    qlQueryDto,
                    // Single searchable/sortable attribute — see the access token sidebar above.
                    WidgetDataParam.create('attributeToDisplay', 'usr.email'),
                    WidgetDataParam.create('showTemplate', true),
                    WidgetDataParam.create('attributeToDisplayTemplate', '@usr#email @e#id'),
                    WidgetDataParam.create('showSearch', true)
                );
            }
        }
    };

    navigationItems: NavigationItem[] = [
        {
            label: 'Zugangstoken',
            action: 'ig.IgAccessToken',
            tooltip: 'API-Zugangstoken verwalten (Gültigkeit, IP-Beschränkung, Nutzungsprotokollierung)',
            icon: 'fa-solid fa-key',
            default: true
        },
        {
            label: 'Zugangstoken-Nutzung',
            action: 'ig.IgAccessTokenUsed',
            tooltip: 'Protokoll der Verwendung von Zugangstoken',
            icon: 'fa-solid fa-clock-rotate-left',
            default: false
        },
        {
            label: 'Pass-Token',
            action: 'ig.IgPassToken',
            tooltip: 'Pass-Token für Endbenutzer verwalten',
            icon: 'fa-solid fa-id-badge',
            default: false
        },
        {
            label: 'Pass-Token-Nutzung',
            action: 'ig.IgPassTokenUsed',
            tooltip: 'Protokoll der Verwendung von Pass-Token',
            icon: 'fa-solid fa-list-check',
            default: false
        }
    ];

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initialized = true;
    }

    override postNavigationSelection() {

        const action = this.activeNavigationItem?.action;

        if (action === 'ig.IgAccessTokenUsed') {
            const accessTokenId = this.route.snapshot.queryParams['accessTokenId'];
            if (accessTokenId) {
                this.refreshAccessTokenUsedWidget(+accessTokenId);
            } else if (!this.accessTokenUsedWidget) {
                this.refreshAccessTokenUsedWidgetAll();
            }
        }

        if (action === 'ig.IgPassTokenUsed') {
            const passTokenId = this.route.snapshot.queryParams['passTokenId'];
            if (passTokenId) {
                this.refreshPassTokenUsedWidget(+passTokenId);
            } else if (!this.passTokenUsedWidget) {
                this.refreshPassTokenUsedWidgetAll();
            }
        }

        // EXIT: a parent selection only has meaning inside its own navigation item. Leaving the
        // item without clearing it would come back as a filtered list whose sidebar shows no
        // selected row — a state the user cannot interpret.
        const paramsToClear: any = {};

        if (action !== 'ig.IgAccessTokenUsed') {
            this.selectedAccessTokenId = null;
            this.accessTokenUsedWidget = null;
            if (this.route.snapshot.queryParams['accessTokenId']) {
                paramsToClear.accessTokenId = null;
            }
        }

        if (action !== 'ig.IgPassTokenUsed') {
            this.selectedPassTokenId = null;
            this.passTokenUsedWidget = null;
            if (this.route.snapshot.queryParams['passTokenId']) {
                paramsToClear.passTokenId = null;
            }
        }

        if (Object.keys(paramsToClear).length > 0) {
            this.router.navigate([], {
                queryParams: paramsToClear,
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }
    }

    /**
     * State A / C of the access token usage log: no token selected, show every record.
     */
    refreshAccessTokenUsedWidgetAll() {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Unfiltered ig.IgAccessTokenUsed log. Sorted by usedOn descending because the
         *   newest access is what a forensic look-up starts from. The entity carries no audit
         *   columns (EntityBase), so usedOn is the only time information available.
         */
        this.accessTokenUsedWidget = WidgetFactory.createWidgetTableEntityQl(
            IgConfigPage.cAccessTokenUsedWidgetAlias,
            'Alle Zugriffe',
            'ig.IgAccessTokenUsed',
            'Keine Zugriffe protokolliert',
            [],
            [new Sorting('e.usedOn', false)],
            true
        );
    }

    /**
     * State B of the access token usage log: filtered to the selected token.
     */
    refreshAccessTokenUsedWidget(accessTokenId: number) {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: ig.IgAccessTokenUsed narrowed to the token picked in the sidebar. Same alias as
         *   the unfiltered state so the user keeps their column layout across selections.
         */
        this.accessTokenUsedWidget = WidgetFactory.createWidgetTableEntityQl(
            IgConfigPage.cAccessTokenUsedWidgetAlias,
            'Zugriffe des gewählten Tokens',
            'ig.IgAccessTokenUsed',
            'Keine Zugriffe protokolliert',
            [FilterCriteria.create('e.accessTokenId', FilterCriteria.cOperatorEqual, accessTokenId)],
            [new Sorting('e.usedOn', false)],
            true
        );
    }

    /**
     * State A / C of the pass token usage log: no token selected, show every record.
     */
    refreshPassTokenUsedWidgetAll() {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Unfiltered ig.IgPassTokenUsed log, sorted by usedOn descending for the same
         *   reason as the access token log.
         */
        this.passTokenUsedWidget = WidgetFactory.createWidgetTableEntityQl(
            IgConfigPage.cPassTokenUsedWidgetAlias,
            'Alle Zugriffe',
            'ig.IgPassTokenUsed',
            'Keine Zugriffe protokolliert',
            [],
            [new Sorting('e.usedOn', false)],
            true
        );
    }

    /**
     * State B of the pass token usage log: filtered to the selected token.
     */
    refreshPassTokenUsedWidget(passTokenId: number) {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: ig.IgPassTokenUsed narrowed to the token picked in the sidebar, same alias as
         *   the unfiltered state so table state survives the selection change.
         */
        this.passTokenUsedWidget = WidgetFactory.createWidgetTableEntityQl(
            IgConfigPage.cPassTokenUsedWidgetAlias,
            'Zugriffe des gewählten Tokens',
            'ig.IgPassTokenUsed',
            'Keine Zugriffe protokolliert',
            [FilterCriteria.create('e.passTokenId', FilterCriteria.cOperatorEqual, passTokenId)],
            [new Sorting('e.usedOn', false)],
            true
        );
    }

    onAccessTokenSelect(e: ObjectIdentifierData | ObjectIdentifier) {
        const event = e as ObjectIdentifierData;
        const accessTokenId = event.data ? event.data['e.id'] : event.objectId;
        this.selectedAccessTokenId = accessTokenId;
        this.refreshAccessTokenUsedWidget(accessTokenId);
        this.router.navigate([], {
            queryParams: {accessTokenId: accessTokenId},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onAccessTokenDeselect() {
        this.selectedAccessTokenId = null;
        this.refreshAccessTokenUsedWidgetAll();
        this.router.navigate([], {
            queryParams: {accessTokenId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onPassTokenSelect(e: ObjectIdentifierData | ObjectIdentifier) {
        const event = e as ObjectIdentifierData;
        const passTokenId = event.data ? event.data['e.id'] : event.objectId;
        this.selectedPassTokenId = passTokenId;
        this.refreshPassTokenUsedWidget(passTokenId);
        this.router.navigate([], {
            queryParams: {passTokenId: passTokenId},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onPassTokenDeselect() {
        this.selectedPassTokenId = null;
        this.refreshPassTokenUsedWidgetAll();
        this.router.navigate([], {
            queryParams: {passTokenId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    handleObjectSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            event.objectType,
            event.objectId,
            'object',
            '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
     * Row click on a sealed usage log. The record is append-only evidence written by the auth
     * path, so it opens read-only rather than in the edit action used for the token tables.
     */
    handleLogObjectSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            event.objectType,
            event.objectId,
            'object',
            '',
            '',
            '',
            MvsObjectNavigationActionEnum.display
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Integration Codes Konfiguration',
                categoryTypeMode: 'ticket',
                icon: 'fa-solid fa-cogs'
            }
        };
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }
}
